// Restricted (c) Siemens Healthineers AG, 2025, All rights reserved.

import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';

import { BackToSystemOverviewComponent } from 'src/app/modules/portal/components/backToSystemOverview/backToSystemOverview.component';
import { CtsGantryInitEvent } from '../../interfaces/cts-gantry-init-event';
import { CtsGantryCacheModel } from '../../model/cts-gantry-cache-model';
import { CtsGantryLogData } from '../../interfaces/cts-gantry-log-data';
import { CtsGantryEventSelectionModel } from '../../model/cts-gantry-event-selection-model';
import { CtsGantryInitComponent } from '../cts-gantry-init/cts-gantry-init.component';
import { APICallGateComponent } from 'src/app/modules/shared/components/api-call-gate.component';
import { CtstatusService } from '../../services/ctstatus.service';
import { TimeFromNowPipe } from 'src/app/modules/shared/pipes/localizedDate.pipe';
import { SystemOverviewService } from 'src/app/modules/portal/services/systemOverview.service';
import { Subject, switchMap, takeUntil } from 'rxjs';
import { CtsGantryTimezoneSelectComponent } from '../cts-gantry-timezone-select/cts-gantry-timezone-select.component';

type CtsActiveDetailsTabType = 'gantryInit';

@Component({
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    TimeFromNowPipe,
    BackToSystemOverviewComponent,
    CtsGantryInitComponent,
    APICallGateComponent,
    CtsGantryTimezoneSelectComponent,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  selector: 'app-cts-gantry-event-details',
  templateUrl: './cts-gantry-event-details.component.html',
  styleUrl: './cts-gantry-event-details.component.scss',
})
export class CtsGantryEventDetailsComponent implements OnInit, OnDestroy {
  protected readonly logCacheModel =
    new CtsGantryCacheModel<CtsGantryLogData>();
  protected readonly selectionModel = new CtsGantryEventSelectionModel();
  protected selectedEvent: CtsGantryInitEvent | undefined;

  protected readonly TAB_GANTRY_INIT = 'gantryInit';

  protected activeTab: CtsActiveDetailsTabType = this.TAB_GANTRY_INIT;

  private readonly ctsystemService = inject(CtstatusService);
  protected gantryEvents = this.ctsystemService.gantryEvents;
  protected gantryInitStatus = this.ctsystemService.gantryEventListInitStatus;
  protected events: CtsGantryInitEvent[] = [];
  protected updatedDate: string = '';
  protected updatedDateOffset: number | undefined = undefined;

  private readonly systemOverviewService = inject(SystemOverviewService);
  private readonly systemDetails = this.systemOverviewService.systemDetails;
  public systemDetails$ = this.systemDetails.lastSuccessfulData$;

  private readonly unsubscribe = new Subject<void>();

  public ngOnInit(): void {
    this.systemDetails$
      .pipe(
        takeUntil(this.unsubscribe),
        switchMap((systemDetails) => {
          return this.gantryEvents.request({
            MaterialNumber: systemDetails.systemMaterialNumber,
            SerialNumber: systemDetails.systemSerialNumber,
          });
        }),
      )
      .subscribe();

    this.systemDetails$
      .pipe(
        takeUntil(this.unsubscribe),
        switchMap((systemDetails) => {
          return this.gantryInitStatus.request({
            MaterialNumber: systemDetails.systemMaterialNumber,
            SerialNumber: systemDetails.systemSerialNumber,
          });
        }),
      )
      .subscribe();

    this.gantryInitStatus.lastSuccessfulData$
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(this.updateDateSubscribeFn);
  }

  public ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  protected updateActiveTab(tab: CtsActiveDetailsTabType): void {
    this.activeTab = tab;
  }

  private readonly updateDateSubscribeFn = (res: any) => {
    this.updatedDate = res.updatedDate!;
    this.updatedDateOffset = res.updatedDateOffset;
  };
}
